import L from 'leaflet'
import FlightsImg from './img/tab-flights.png'
import WeatherImg from './img/tab-weather.png'
import AirlinesImg from './img/tab-airlines.png'
import SettingsImg from './img/tab-settings.png'
import RainImg from './img/weather-rain.png'
import MixedImg from './img/weather-mixed.png'
import SnowImg from './img/weather-snow.png'
import { getAirlineName } from '../utils'
import { FlightViewMapLibMap, mapStyles } from '../map'
import { FlightViewMapLibATM } from '.'
import { t } from 'i18next'
import { format } from 'date-fns'
import { getLocale } from '../tracker/utils'
import { SortAirline, Substitutions } from '../types'

enum TabType {
  Flights = 'FVLib-atm-legend-flights',
  Weather = 'FVLib-atm-legend-weather',
  Airlines = 'FVLib-atm-legend-airlines',
  Settings = 'FVLib-atm-legend-settings'
}

const host = location.host.endsWith('flightview.com') ? location.host : 'tracker.flightview.com'

export class FlightViewMapLibATMLegend {
  atm: FlightViewMapLibATM
  map: L.Map
  airlines = [] as string[]
  checkedAirlines = [] as string[]
  timeFormat: string
  dateFormat: string
  locale: string
  displayClock: boolean = true
  legendPosition: string
  sortAirlines: SortAirline[] = []
  legendAirlineSorting: string
  substitutions: Substitutions

  constructor (atm: FlightViewMapLibATM, airlines: string[], departureFlightsLayer: L.LayerGroup, arrivalFlightsLayer: L.LayerGroup, mapStyle: string, mapModule: FlightViewMapLibMap, interactive: boolean, timeFormat: string, dateFormat: string, locale: string, substitutions: Substitutions) {
    this.atm = atm
    this.map = atm.map
    this.airlines = [...airlines].sort()
    this.checkedAirlines = [...airlines].sort()
    this.timeFormat = timeFormat || 'h:mm a'
    this.dateFormat = dateFormat || 'MMM d'
    this.locale = locale
    this.displayClock = atm.props.displayClock !== undefined ? atm.props.displayClock : true
    this.legendPosition = atm.props.legendPosition || 'bottomleft'
    this.legendAirlineSorting = atm.props.legend?.airlines?.sorting || 'airlineCode'
    this.substitutions = substitutions

    const displayDepartures = atm.displayDepartures == undefined || atm.displayDepartures
    const displayArrivals = atm.displayArrivals == undefined || atm.displayArrivals

    const legend = L.Control.extend({
      options:
        {
          position: this.legendPosition
        },
      onAdd: () => {
        const container = L.DomUtil.create('div', 'FVLib-atm-legend')
        L.DomEvent.disableClickPropagation(container)
        L.DomEvent.disableScrollPropagation(container)
        if (interactive) {
          const content = L.DomUtil.create('div', 'FVLib-atm-legend-content')
          content.id = 'FVLib-atm-legend-content'
          const header = L.DomUtil.create('div', 'FVLib-atm-legend-content-header', content)
          header.id = 'FVLib-atm-legend-content-header'
          const close = L.DomUtil.create('a', 'close', header)
          close.id = 'FVLib-atm-legend-content-close'
          close.innerHTML = '&times;'
          container.appendChild(content)
          const buttons = L.DomUtil.create('div', 'FVLib-atm-legend-tabs')
          container.appendChild(buttons)
          const flightsButton = this.createButton(FlightsImg, t('ATM.FLIGHTS_TAB.LABEL'), TabType.Flights, content)
          flightsButton.classList.add('FVLib-atm-legend-tab-first')
          buttons.appendChild(flightsButton)
          const weatherButton = this.createButton(WeatherImg, t('ATM.WEATHER_TAB.LABEL'), TabType.Weather, content)
          buttons.appendChild(weatherButton)
          buttons.appendChild(this.createButton(AirlinesImg, t('ATM.AIRLINES_TAB.LABEL'), TabType.Airlines, content))
          const settingsButton = this.createButton(SettingsImg, t('ATM.SETTINGS_TAB.LABEL'), TabType.Settings, content)
          settingsButton.classList.add('FVLib-atm-legend-tab-last')
          buttons.appendChild(settingsButton)
          content.appendChild(createWeatherContent())
          content.appendChild(this.createFlightsContent(departureFlightsLayer, arrivalFlightsLayer, interactive))
          this.getSortAirlineNameList().then(() => {
            content.appendChild(this.createAirlinesContent(interactive))
          }).catch(err => {
            console.error(err)
          })
          content.appendChild(this.createSettingsContent(mapStyle, mapModule))
        } else {
          container.className = 'FVLib-atm-static-legend'
          container.id = 'FVLib-atm-static-legend'
          container.appendChild(this.createFlightsContent(displayDepartures ? departureFlightsLayer : null, displayArrivals ? arrivalFlightsLayer : null, interactive))
          const tempAirlinesNode = L.DomUtil.create('div')
          container.appendChild(tempAirlinesNode)
          this.getSortAirlineNameList().then(() => {
            container.replaceChild(this.createAirlinesContent(interactive), tempAirlinesNode)
          }).catch(err => {
            console.error(err)
          })
          if (this.displayClock) {
            container.appendChild(this.createDateTimeContent())
            this.scheduleUpdateTime()
          }
          container.appendChild(this.createOAGLogo())
        }
        return container
      }
    })
    const instance = new legend()
    this.map.addControl(instance)
  }

  createAirlinesContent (interactive: boolean): HTMLElement {
    const div = L.DomUtil.create('div', TabType.Airlines)
    for (const airline of this.sortAirlines) {
      const airlineRow = L.DomUtil.create('div', 'FVLib-atm-legend-airlines-row', div)
      if (interactive) airlineRow.appendChild(this.createAirlineCheckbox(true, airline.code))
      const logo = L.DomUtil.create('img', 'FVLib-atm-legend-airlines-row-image', airlineRow)
      logo.src = `https://${host}/images/AirlineLogo/DHTML_ATL/100x80/${airline.code}.png`
      logo.onerror = () => {
        logo.src = `https://${host}/FVAccess3/res/img/FlightFinder/AirlineLogoPNG/50x40/dummy.png`
      }
      logo.alt = `${airline.code} logo`
      if (!interactive) {
        const labelCode = L.DomUtil.create('span', 'FVLib-atm-legend-airlines-row-label-code', airlineRow)
        labelCode.innerText = airline.code
      }
      const label = L.DomUtil.create('span', 'FVLib-atm-legend-airlines-row-label', airlineRow)
      label.innerText = airline.name
      logo.alt = airline.code
    }
    if (!interactive) div.className = 'FVLib-atm-static-legend-airlines FVLib-atm-static-legend-content'
    return div
  }

  async getSortAirlineNameList () {
    for (const airline of this.airlines) {
      const sortAirline: any = {}
      let name: string = ''
      sortAirline.code = airline
      if (this.substitutions?.airlineLabels?.[airline]) {
        name = this.substitutions.airlineLabels[airline]
      } else {
        name = await getAirlineName(airline)
      }
      sortAirline.name = name
      this.sortAirlines.push(sortAirline)
    }
    this.sortAirlines.sort((a, b) => this.legendAirlineSorting === 'airlineName' ? a.name.localeCompare(b.name) : a.code.localeCompare(b.code))
  }

  createAirlineCheckbox (checked: boolean, airline: string): HTMLElement {
    const airlineCheckbox = L.DomUtil.create('input')
    airlineCheckbox.type = 'checkbox'
    airlineCheckbox.checked = checked
    airlineCheckbox.addEventListener('change', e => {
      const target = e.target as HTMLInputElement
      if (target.checked) {
        this.checkedAirlines.push(airline)
      } else {
        this.checkedAirlines = this.checkedAirlines.filter(item => item !== airline)
      }
      void this.atm.drawAllPlanes(this.checkedAirlines)
    }, false)
    return airlineCheckbox
  }

  createFlightsContent (depFlightsLayer: L.LayerGroup, arrFlightsLayer: L.LayerGroup, interactive: boolean): HTMLElement {
    const div = L.DomUtil.create('div', TabType.Flights)
    div.className = interactive ? div.className : 'FVLib-atm-static-legend-flights FVLib-atm-static-legend-content';

    if(depFlightsLayer){
      const depPlaneRow = L.DomUtil.create('div', 'FVLib-atm-legend-flights-row', div)
      const depPlaneImg = L.DomUtil.create('img', 'FVLib-atm-legend-flights-departures-image', depPlaneRow)
      depPlaneImg.src = this.atm.planeIconDepartureUrl
      const depPlaneLabel = L.DomUtil.create('span', 'FVLib-atm-legend-flights-departures-label', depPlaneRow)
      depPlaneLabel.innerText = t('ATM.FLIGHTS_TAB.DEPARTURES')
      if(interactive) {
        depPlaneRow.appendChild(this.createFlightCheckbox(depFlightsLayer, 'FVLib-atm-legend-flights-departures-checkbox', 'Departures'))
      } else {
        depPlaneImg.style.transform = 'none';
      }
    }

    if(arrFlightsLayer) {
      const arrPlaneRow = L.DomUtil.create('div', 'FVLib-atm-legend-flights-row', div)
      const arrPlaneImg = L.DomUtil.create('img', 'FVLib-atm-legend-flights-arrivals-image', arrPlaneRow)
      arrPlaneImg.src = this.atm.planeIconArrivalUrl
      const arrPlaneLabel = L.DomUtil.create('span', 'FVLib-atm-legend-flights-arrivals-label', arrPlaneRow)
      arrPlaneLabel.innerText = t('ATM.FLIGHTS_TAB.ARRIVALS')
      if(interactive) {
        arrPlaneRow.appendChild(this.createFlightCheckbox(arrFlightsLayer, 'FVLib-atm-legend-flights-arrivals-checkbox', 'Arrivals'))
      } else {
        arrPlaneImg.style.transform = 'none';
      }
    }
    return div
  }

  createFlightCheckbox (layer: L.LayerGroup, className: string, direction: 'Departures' | 'Arrivals'): HTMLElement {
    const checkbox = L.DomUtil.create('input', className)
    checkbox.type = 'checkbox'
    checkbox.checked = direction === 'Departures' ? this.atm.displayDepartures : this.atm.displayArrivals
    checkbox.addEventListener('change', e => {
      const target = e.target as HTMLInputElement
      direction === 'Departures' ? this.atm.displayDepartures = target.checked : this.atm.displayArrivals = target.checked
      void this.atm.drawAllPlanes(this.checkedAirlines)
    }, false)
    return checkbox
  }

  onPolylineCreated (ply: L.Polyline) {
    ply.setStyle({
      weight: 1
    })
  }

  createSettingsContent (mapStyle: string, mapModule: FlightViewMapLibMap): HTMLElement {
    const div = L.DomUtil.create('div', TabType.Settings)
    const firstChildDiv = L.DomUtil.create('div', 'FVLib-atm-legend-settings-first-child', div)
    const metricScale = L.DomUtil.create('input', '', firstChildDiv)
    metricScale.type = 'checkbox'
    metricScale.onchange = (e) => {
      const target = e.target as HTMLInputElement
      if (target.checked) {
        this.map.removeControl(mapModule.imperialScale)
        this.map.addControl(mapModule.metricScale)
      } else {
        this.map.removeControl(mapModule.metricScale)
        this.map.addControl(mapModule.imperialScale)
      }
    }
    const metricLabel = L.DomUtil.create('span', '', firstChildDiv)
    metricLabel.innerText = t('ATM.SETTINGS_TAB.METRIC_SCALE')
    const secondChildDiv = L.DomUtil.create('div', 'FVLib-atm-legend-settings-second-child', div)
    mapStyles.forEach((style) => {
      const mapStyleItemDiv = L.DomUtil.create('div', 'FVLib-atm-legend-settings-mapstyle-item', secondChildDiv)
      const mapStyleRadio = L.DomUtil.create('input', '', mapStyleItemDiv)
      mapStyleRadio.type = 'radio'
      mapStyleRadio.name = 'mapStyle'
      mapStyleRadio.defaultChecked = style.name === mapStyle
      mapStyleRadio.onchange = () => {
        mapModule.addMapTileLayer(style.name)
      }
      const mapStyleLabel = L.DomUtil.create('span', '', mapStyleItemDiv)
      mapStyleLabel.innerText = style.label
    })
    return div
  }

  createButton (imgUrl: string, label: string, type: TabType, contentElement: HTMLElement): HTMLElement {
    const div = L.DomUtil.create('div', 'FVLib-atm-legend-tab')
    const img = L.DomUtil.create('img', 'FVLib-atm-legend-tab-image', div)
    img.src = imgUrl
    const span = L.DomUtil.create('span', 'FVLib-atm-legend-tab-label', div)
    span.innerText = label
    div.addEventListener('click', e => {
      this.onTabClick(contentElement, type, e.currentTarget as HTMLElement)
    }, false)
    return div
  }

  onTabClick (contentElement: HTMLElement, tab: TabType, caller: HTMLElement) {
    this.removeActiveElements()
    const children = contentElement.children
    for (let i = 0; i < children.length; i++) {
      const child = children[i] as HTMLElement
      if (child.className === tab) {
        child.style.display = 'flex'
        caller.classList.add('FVLib-atm-legend-active-tab')
      } else {
        child.style.display = 'none'
      }
    }
    const headerContainer = document.getElementById('FVLib-atm-legend-content-header')
    headerContainer.style.display = 'flex'
    const closeElement = document.getElementById('FVLib-atm-legend-content-close')
    closeElement.addEventListener('click', () => {
      this.onClose(contentElement)
    }, false)
  }

  onClose (contentElement: HTMLElement) {
    this.removeActiveElements()
    const children = contentElement.children
    for (let i = 0; i < children.length; i++) {
      const child = children[i] as HTMLElement
      child.style.display = 'none'
    }
  }

  removeActiveElements () {
    const activeElements = document.getElementsByClassName('FVLib-atm-legend-active-tab')
    for (let i = 0; i < activeElements.length; i++) {
      const element = activeElements[i]
      element.classList.remove('FVLib-atm-legend-active-tab')
    }
  }

  scheduleUpdateTime () {
    setInterval(() => this.updateCurrentTime(), 60 * 1000)
  }

  createDateTimeContent () {
    const div = L.DomUtil.create('div', 'FVLib-atm-static-legend-datetime FVLib-atm-static-legend-content')
    const dateTxt = L.DomUtil.create('span', 'FVLib-atm-legend-date FVLib-atm-legend-datetime-label')
    dateTxt.id = 'FVLib-atm-legend-date'
    div.appendChild(dateTxt)

    const timeDiv = L.DomUtil.create('div', 'FVLib-atm-legend-time')
    const timeTxt = L.DomUtil.create('span', 'FVLib-atm-legend-datetime-label')
    timeTxt.id = 'FVLib-atm-legend-time'
    timeDiv.appendChild(timeTxt)

    const meridiemTxt = L.DomUtil.create('span', 'FVLib-atm-legend-meridiem-label')
    meridiemTxt.id = 'FVLib-atm-legend-meridiem'
    timeDiv.appendChild(meridiemTxt)

    div.appendChild(timeDiv)
    this.updateCurrentTime(dateTxt, timeTxt, meridiemTxt)
    return div
  }

  createOAGLogo () {
    const div = L.DomUtil.create('div', 'FVLib-atm-static-legend-oag-logo FVLib-atm-static-legend-content')
    const img = L.DomUtil.create('img', 'FVLib-oagLogo')
    img.src = `https://${host}/images/Logos/OAG/OAG_436x274.png`
    img.style.display = 'block'
    div.appendChild(img)
    return div
  }

  updateCurrentTime (dateTxtEle?: HTMLElement, timeTxtEle?: HTMLElement, meridiemTxtEle?: HTMLElement) {
    const now = new Date()
    const dateTxt = format(now, this.dateFormat, { locale: getLocale(this.locale) })
    const timeTxt = format(now, this.timeFormat, { locale: getLocale(this.locale) })
    dateTxtEle = dateTxtEle || document.getElementById('FVLib-atm-legend-date')
    dateTxtEle.innerText = dateTxt
    timeTxtEle = timeTxtEle || document.getElementById('FVLib-atm-legend-time')
    timeTxtEle.innerText = timeTxt.split(' ')[0]
    meridiemTxtEle = meridiemTxtEle || document.getElementById('FVLib-atm-legend-meridiem')
    meridiemTxtEle.innerText = timeTxt.split(' ')[1] || ''
  }
}

export function createWeatherContent () {
  const div = L.DomUtil.create('div', TabType.Weather)

  const left = L.DomUtil.create('div', 'FVLib-atm-legend-weather-left', div)
  const rainLabel = L.DomUtil.create('span', null, left)
  rainLabel.innerText = t('ATM.WEATHER_TAB.RAIN')
  const mixedLabel = L.DomUtil.create('span', null, left)
  mixedLabel.innerText = t('ATM.WEATHER_TAB.MIXED')
  const snowLabel = L.DomUtil.create('span', null, left)
  snowLabel.innerText = t('ATM.WEATHER_TAB.SNOW')
  const emptyLabel = L.DomUtil.create('div', 'FVLib-atm-legend-weather-spacer', left)
  emptyLabel.innerText = ''

  const right = L.DomUtil.create('div', 'FVLib-atm-legend-weather-right', div)
  const rainImage = L.DomUtil.create('img', 'FVLib-atm-legend-weather-image', right)
  rainImage.src = RainImg
  const mixedImage = L.DomUtil.create('img', 'FVLib-atm-legend-weather-image', right)
  mixedImage.src = MixedImg
  const snowImage = L.DomUtil.create('img', 'FVLib-atm-legend-weather-image', right)
  snowImage.src = SnowImg
  const bottomLabels = L.DomUtil.create('div', 'FVLib-atm-legend-weather-bottom', right)
  const lightLabel = L.DomUtil.create('span', null, bottomLabels)
  lightLabel.innerText = t('ATM.WEATHER_TAB.LIGHT')
  const heavyLabel = L.DomUtil.create('span', null, bottomLabels)
  heavyLabel.innerText = t('ATM.WEATHER_TAB.HEAVY')

  return div
}
